'use client'

import type { AnimationTiming } from 'recharts/types/util/types'

import { useCallback, useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { variantStyles } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

const DEFAULT_BAR_SIZE = 24

// 'auto' lets recharts size bars from the container width and category count
export type BarSize = number | 'auto'

interface BarConfig {
  key: string
  dataKey: string
  fill: string
  stackId?: string
  radius?: [number, number, number, number]
  shape?: ((props: unknown) => React.JSX.Element) | undefined
  animationBegin?: number
  animationDuration?: number
  animationEasing?: AnimationTiming
}

interface MultipleBarChartProps<T> {
  data: T[]
  xAxisKey: string
  bars: BarConfig[]
  tooltipFormatter?: (dataPoint: T) => React.ReactNode
  barSize?: BarSize
  onBarClick?: (dataPoint: T) => void
  hideYAxis?: boolean
  yAxisFormatter?: (value: number) => string
  barChartClassName?: string
  /** Inset for the first/last bands so edge bars don't touch the container. */
  xAxisPadding?: { left?: number; right?: number }
}

const BarChartNoData = () => {
  return (
    <Text
      style={{ transform: `translate(50%, 50%)` }}
      x={0}
      y={0}
      textAnchor="middle"
      verticalAnchor="middle"
      className={cn(variantStyles.bodySmall)}
    >
      No data available
    </Text>
  )
}

// biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
const CustomXAxisTick = ({ x, y, payload }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        fontSize={11}
        className="fill-secondary"
        textAnchor="middle"
      >
        {payload.value}
      </text>
    </g>
  )
}

// biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
const CustomYAxisTick = ({ x, y, payload, yAxisFormatter }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        dx={-16}
        fontSize={11}
        className="fill-secondary"
        textAnchor="middle"
      >
        {yAxisFormatter ? yAxisFormatter(payload.value) : payload.value}
      </text>
    </g>
  )
}

export function MultipleBarChart<T>({
  data,
  xAxisKey,
  bars,
  tooltipFormatter,
  barSize = DEFAULT_BAR_SIZE,
  onBarClick,
  hideYAxis = true,
  yAxisFormatter,
  barChartClassName,
  xAxisPadding,
}: MultipleBarChartProps<T>) {
  const hasNoData = useMemo(() => data.length === 0, [data])

  const resolvedBarSize = barSize === 'auto' ? undefined : barSize

  const handleBarClick = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
    (data: any) => {
      if (onBarClick && data) {
        onBarClick(data as T)
      }
    },
    [onBarClick],
  )

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        barSize={resolvedBarSize}
        margin={{ top: 10, bottom: 14 }}
        className={barChartClassName}
      >
        {hasNoData ? (
          <Customized component={<BarChartNoData />} />
        ) : (
          <>
            <XAxis
              dataKey={xAxisKey}
              axisLine={false}
              tickLine={false}
              scale="auto"
              tick={<CustomXAxisTick />}
              padding={xAxisPadding}
            />
            <YAxis
              axisLine={hideYAxis}
              hide={hideYAxis}
              tickLine={hideYAxis}
              tick={<CustomYAxisTick yAxisFormatter={yAxisFormatter} />}
            />
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              horizontal={!hideYAxis}
            />
            <Tooltip
              content={
                tooltipFormatter
                  ? (props) => {
                      if (!props.payload || props.payload.length === 0)
                        return null
                      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                      return tooltipFormatter(props.payload[0]!.payload as T)
                    }
                  : undefined
              }
              cursor={false}
              animationDuration={50} // in milliseconds
            />

            {bars.map((bar) => (
              <Bar
                key={bar.key}
                dataKey={bar.dataKey}
                stackId={bar.stackId}
                fill={bar.fill}
                radius={bar.radius}
                shape={bar.shape}
                animationBegin={bar.animationBegin}
                animationDuration={bar.animationDuration}
                animationEasing={bar.animationEasing}
                onClick={onBarClick ? handleBarClick : undefined}
                cursor={onBarClick ? 'pointer' : undefined}
              />
            ))}
          </>
        )}
      </BarChart>
    </ResponsiveContainer>
  )
}
